import { inject } from '@angular/core';
import { selectRouteUrl } from '@mkp/shared/util-router';
import { SplashscreenService } from '@mkp/shared/util-splashscreen';
import { selectSelectedAccountId } from '@mkp/account/state';
import {
  authActions,
  authApiActions,
  authOnboardingRecoveryActions,
  authWorkspaceActions,
} from '@mkp/auth/actions';
import { AuthFacade, Token } from '@mkp/auth/util';
import { FullviewService, ZendeskService } from '@mkp/shared/util-zendesk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { selectIsBoUser, selectLoggedInUser } from '@mkp/user/state-logged-in/selectors';
import { jwtDecode } from 'jwt-decode';
import { ZendeskJwt } from '@mkp/shared/util-zendesk';
import { distinctUntilChanged, of, pairwise } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { AccountProtectionService } from '@mkp/shared/util-router';
import { isBoUserFn } from './helpers/auth-effects.helpers';

export const appBootstrapped = createEffect(
  (
    actions$ = inject(Actions),
    authFacade = inject(AuthFacade),
    store = inject(Store),
    splashscreen = inject(SplashscreenService)
  ) =>
    actions$.pipe(
      ofType(authActions.appBootstrapped),
      switchMap(() =>
        authFacade.isAuthenticated$.pipe(
          switchMap((logged: boolean) => (logged ? of(logged) : authFacade.fetchAccessToken())),
          filter((logged: boolean | Token) => !!logged),
          switchMap(() => authFacade.isVerified$),
          concatLatestFrom(() => store.select(selectLoggedInUser).pipe(filter(Boolean))),
          map(([isVerified, user]) =>
            isVerified
              ? authActions.verifiedUserAuthenticated({ userId: user.id })
              : authActions.unverifiedUserAuthenticated()
          ),
          catchError((error) => {
            splashscreen.hide();
            return of(authActions.appBootstrapFailed({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const onboardUser = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(authActions.loadLegalEntityForUserWithNoAccountFailed),
      concatLatestFrom(() => store.select(selectIsBoUser)),
      filter(([_, isBoUser]) => isBoUser !== null && !isBoUser),
      map(() => authWorkspaceActions.workspaceSelectionNotRequired())
    ),
  { functional: true }
);

// cf JMP-4357
// when we fail to download the initial account we want to logout the user and display an error
// auth0 refreshes the page after a logout, so we need to set a flag in session to communicate that there was an error
export const forceLogoutWhenFailingToLoadInitialAccount = createEffect(
  (actions$ = inject(Actions), authFacade = inject(AuthFacade)) =>
    actions$.pipe(
      ofType(
        authActions.loadActiveAccountMembershipsFailed,
        authActions.loadAccountsAfterLoginFailed,
        authActions.loadCompaniesAfterLoginFailed,
        authOnboardingRecoveryActions.onboardingRecoveryFailed
      ),
      tap(() => sessionStorage.setItem('forcedLogout', 'true')),
      // TODO: Will be redirected to an internal-error route which we don't have yet
      tap(() => authFacade.logout())
    ),
  { functional: true, dispatch: false }
);

export const updateZendeskToken = createEffect(
  (actions$ = inject(Actions), zendeskService = inject(ZendeskService)) =>
    actions$.pipe(
      ofType(authApiActions.fetchAccessTokenSuccess),
      tap(({ zendeskJWT }) => zendeskService.login(zendeskJWT))
    ),
  { functional: true, dispatch: false }
);

export const setFullviewSalesforceId = createEffect(
  (actions$ = inject(Actions), fullviewService = inject(FullviewService)) =>
    actions$.pipe(
      ofType(authApiActions.fetchAccessTokenSuccess),
      map(({ zendeskJWT }) => jwtDecode<ZendeskJwt>(zendeskJWT)?.external_id),
      tap((externalId) => fullviewService.setFullviewSalesforceId(externalId ?? ''))
    ),
  { functional: true, dispatch: false }
);

export const redirectBoUserFromRestrictedRoutes = createEffect(
  (store = inject(Store), accountProtectionService = inject(AccountProtectionService)) =>
    store.select(selectSelectedAccountId).pipe(
      distinctUntilChanged(),
      // A cold load reads undefined -> the workspace named in the URL: the page restoring
      // itself, not a switch. Treating it as one bounced BO users off every reload (SEE-565).
      pairwise(),
      filter(([previousAccountId]) => previousAccountId !== undefined),
      concatLatestFrom(() => [store.select(selectIsBoUser), store.select(selectRouteUrl)]),
      filter(([, isBoUser]) => isBoUserFn(isBoUser)),
      map(([, , currentUrl]) => currentUrl),
      filter(Boolean),
      tap((currentUrl) => accountProtectionService.checkCurrentUrl(currentUrl))
    ),
  { functional: true, dispatch: false }
);
